import React, { useState } from "react";
import SectionContainer from "../../../Homepage/SectionContainer";
import { Col, Container, Row } from "reactstrap";
import BreadCrumb from "../../../component/BreadCrumb/BreadCrumb";
import "../Blog.css";
import { Link, useNavigate, useParams } from "react-router-dom";
import Map from "../../../Homepage/Map.jsx";
import SmallPage from "../../../Homepage/SmallPage.jsx";
import { imageBaseApi } from "../../../services/imageBaseApi.js";
import BlogService from "../../../services/BlogService.js";
import { useQuery } from "@tanstack/react-query";
import { formatDate } from "../../../common/formatedate.js";
import { ChevronLeft, ChevronRight } from "lucide-react";

const ITEMS_PER_PAGE = 6;

const Blog = () => {
  const router = useNavigate();
  const { id } = useParams(); // This is actually the slug
  const [currentPage, setCurrentPage] = useState(1);

  // -------- Fetch single blog --------
  const fetchBlogData = async () => {
    const response = await BlogService.getbyId(id);
    return response;
  };

  const {
    isPending,
    error,
    data: blogData,
  } = useQuery({
    queryKey: ["blogDetail", id],
    queryFn: fetchBlogData,
  });

  // -------- Fetch all blogs --------
  const fetchBlogs = async ({ queryKey }) => {
    const [_key, currentPage] = queryKey;

    return BlogService.getList({
      page: currentPage,
      pageSize: ITEMS_PER_PAGE,
      status: 2
    });
  };

  const {
    isLoading,
    error: blogerror,
    data: blogResponse,
  } = useQuery({
    queryKey: ["blogList", currentPage],
    queryFn: fetchBlogs,
    keepPreviousData: true,
  });

  const totalPages = blogResponse?.totalPages || 1;

  if (isPending) return <p>Loading...</p>;
  if (error) return <p>Error loading blog</p>;
  if (!blogData) return <p>Blog not found</p>;

  // -------- Filter out current blog using the blog's actual ID --------
  const filteredBlogs = Array.isArray(blogResponse?.items)
    ? blogResponse.items.filter((blog) => blog?.id !== blogData.id)
    : [];

  // -------- Pagination handler --------
  const changePage = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const getPageButtonStyle = (isActive) => ({
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    border: "none",
    fontWeight: "500",
    cursor: "pointer",
    transition: "all 0.3s ease",
    background: isActive ? "#7e1790" : "transparent",
    color: isActive ? "white" : "#374151",
    transform: isActive ? "scale(1.1)" : "scale(1)",
  });

  // -------- Render HTML content --------
  const renderContent = (htmlContent) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlContent;

    return Array.from(tempDiv.children).map((child, index) => {
      if (child.tagName === "H1") {
        return (
          <Row key={index} >
            <Col>
              <h1 className="font-level-4 lh-1" style={{marginTop:"2rem"}}>{child.textContent}</h1>
            </Col>
          </Row>
        );
      }

      if (child.tagName === "H2") {
        return (
          <Row key={index} >
            <Col>
              <h1 className="font-level-8" style={{marginTop:"2rem"}}>{child.textContent}</h1>
            </Col>
          </Row>
        );
      }

      if (child.tagName === "H3") {
        return (
          <Row key={index} >
            <Col>
              <h1 className="font-level-7" style={{marginTop:"2rem"}}>{child.textContent}</h1>
            </Col>
          </Row>
        );
      }



      if (child.tagName === "P" && child.textContent.trim()) {
        return (
          <Row key={index}>
            <Col>
              <p
                className="country-paragraph-text mb-3"
                dangerouslySetInnerHTML={{
                  __html: child.innerHTML,
                }}
              />
            </Col>
          </Row>
        );
      }

      if (child.tagName === "UL") {
        return (
          <Row key={index}>
            <Col>
              <ul
                className="country-paragraph-text mb-3"
                dangerouslySetInnerHTML={{
                  __html: child.innerHTML,
                }}
              />
            </Col>
          </Row>
        );
      }

      if (child.tagName === "OL") {
        return (
          <Row key={index}>
            <Col>
              <ol
                className="country-paragraph-text mb-3"
                dangerouslySetInnerHTML={{
                  __html: child.innerHTML,
                }}
              />
            </Col>
          </Row>
        );
      }

      return null;
    });
  };

  return (
    <>
      <SectionContainer>
        <Container className="p-0">
          <Row>
            <Col>
              <BreadCrumb
                items={[
                  { title: "HOME", link: "/" },
                  { title: "BLOG", link: "/blog" },
                  { title: "DETAIL" },
                ]}
                instituition={false}
              />
            </Col>
          </Row>

          <Row>
            <Col>
              <h1 className="font-level-4 lh-1 mb-5 mt-1">{blogData.title}</h1>
            </Col>
          </Row>

          <Row>
            <Col
              className="rounded-5"
              style={{ height: "450px", marginBottom: "45px" }}
            >
              <img
                loading="lazy"
                decoding="async"
                src={`${imageBaseApi}${blogData.coverMedia?.url}`}
                alt={blogData.title}
                className="rounded-5"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            </Col>
          </Row>

          <div className="mb-3">
            <small className="text-muted">
              Published on {formatDate(blogData.publishedAt)}
            </small>
          </div>

          {blogData.content && renderContent(blogData.content)}

          <Row>
            <Col>
              <h1
                className="font-level-8"
                style={{ marginTop: "11rem", marginBottom: "4rem" }}
              >
                Recent Posts
              </h1>
            </Col>
          </Row>

          <Row className="d-flex" style={{ marginRight: "-6%" }}>
            {filteredBlogs?.map((blog, index) => (
              <React.Fragment key={blog?.id || index}>
                <Col
                  lg={4}
                  md={6}
                  sm={12}
                  className="d-flex flex-column"
                  style={{ cursor: "pointer" }}
                >
                  <Link
                    to={`/blog/${blog?.slug}`}
                    className="text-decoration-none"
                  >
                    <div
                      style={{ width: "90%", height: "247px" }}
                      className="rounded-5"
                    >
                      <img
                        loading="lazy"
                        src={`${imageBaseApi}${blog?.thumbnailMedia?.url}`}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                        fluid
                        className="rounded-5 img-fluid"
                        alt={blog?.title}
                      />
                    </div>
                    <p className="blog-desc" style={{ marginTop: "24px" }}>
                      Published On: {formatDate(blog?.publishedAt)}
                    </p>
                    <div style={{ width: "90%" }}>
                      <h5 className="recent-title">{blog?.title}</h5>
                    </div>
                    <p
                      className="country-paragraph-text m-0"
                      style={{ width: "90%" }}
                    >
                      {blog?.content
                        ?.replace(/<[^>]*>/g, "")
                        .replace(/\s+/g, " ")
                        .trim()
                        .split(" ")
                        .slice(0, 25)
                        .join(" ")}{" "}
                      [...]
                    </p>
                  </Link>
                </Col>

                {(index + 1) % 3 === 0 && (
                  <Col xs={12}>
                    <hr
                      className={
                        index + 1 === filteredBlogs.length
                          ? "line-style-testprep"
                          : "line"
                      }
                    />
                  </Col>
                )}
              </React.Fragment>
            ))}
          </Row>

          {totalPages > 1 && (
            <div
              className="w-100 d-flex justify-content-end"
              style={{ marginTop: "20px", marginBottom: "13rem" }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                {/* Previous */}
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "36px",
                    height: "36px",
                    borderRadius: "50%",
                    border: "none",
                    background: "transparent",
                    color: "#6b7280",
                    cursor: currentPage === 1 ? "not-allowed" : "pointer",
                    opacity: currentPage === 1 ? 0.4 : 1,
                  }}
                >
                  <ChevronLeft size={18} />
                </button>

                {/* Page numbers with ellipsis logic */}
                {(() => {
                  const maxVisible = 5;
                  let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
                  let end = Math.min(totalPages, start + maxVisible - 1);

                  if (end - start + 1 < maxVisible) {
                    start = Math.max(1, end - maxVisible + 1);
                  }

                  const showPages = [];

                  if (start > 1) {
                    showPages.push(
                      <button
                        key={1}
                        onClick={() => setCurrentPage(1)}
                        style={getPageButtonStyle(1 === currentPage)}
                      >
                        1
                      </button>
                    );
                    if (start > 2) {
                      showPages.push(<span key="left-ellipsis">...</span>);
                    }
                  }

                  for (let page = start; page <= end; page++) {
                    showPages.push(
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        style={getPageButtonStyle(page === currentPage)}
                      >
                        {page}
                      </button>
                    );
                  }

                  if (end < totalPages) {
                    if (end < totalPages - 1) {
                      showPages.push(<span key="right-ellipsis">...</span>);
                    }
                    showPages.push(
                      <button
                        key={totalPages}
                        onClick={() => setCurrentPage(totalPages)}
                        style={getPageButtonStyle(totalPages === currentPage)}
                      >
                        {totalPages}
                      </button>
                    );
                  }

                  return showPages;
                })()}

                {/* Next */}
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "36px",
                    height: "36px",
                    borderRadius: "50%",
                    border: "none",
                    background: "transparent",
                    color: "#6b7280",
                    cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                    opacity: currentPage === totalPages ? 0.4 : 1,
                  }}
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          )}
        </Container>
      </SectionContainer>

      <hr className="line" />

      <SectionContainer>
        <Map />
        <hr className="line-style-testprep" />
        <SmallPage countryPage={true} />
      </SectionContainer>
    </>
  );
};

export default Blog;